import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Tabs as MuiTabs, Tab, Stack, Button, Divider, Typography, Select, MenuItem, FormControl, Tooltip } from "@mui/material";
import { globalTheme } from "../../styles";
import { AdvancedSearchIcon } from "../Icon";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import { ORDER_SHIPMENTS_SEARCH_TYPES, SEARCH_TEXT, SHIPMENTS_TAB, SHIPMENTS_TABLE_TABS } from "../../constants";

const AntTabs = styled(MuiTabs)({
  borderBottom: `1px solid ${globalTheme.border}`,
  "& .MuiTabs-indicator": {
    backgroundColor: globalTheme.blue,
  },
});

const AntTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  "&:hover": {
    color: globalTheme.blue,
    opacity: 1,
  },
  "&.Mui-selected": {
    color: globalTheme.blue,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

interface StyledTabProps {
  label: string;
  value: string;
}
interface TabsProps {
  onAdvancedSettingsOpen: () => void;
  onOrderShipmentsCustomSearch: (customSearchText: string, searchType: string) => void;
  onShipmentTabChange: (tabId: string) => void;
}

export const ShipmentTabs = ({ onAdvancedSettingsOpen, onOrderShipmentsCustomSearch, onShipmentTabChange }: TabsProps) => {
  //Used this condition to sync the submenu highlight which reflects in primary nav
  const storedTabValue = localStorage.getItem(SHIPMENTS_TAB);
  if (!storedTabValue) {
    localStorage.setItem(SHIPMENTS_TAB, SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.value);
  }
  const [shipmentTabValue, setShipmentValue] = useState(storedTabValue || SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.value);

  const handleShipmentsTabChange = (event: React.SyntheticEvent, newValue: string) => {
    localStorage.setItem(SHIPMENTS_TAB, newValue);
    setShipmentValue(newValue);
    onShipmentTabChange(newValue);
  };

  const [orderShipmentsSearchText, setOrderShipmentsSearchText] = useState<string>("");
  const [searchType, setSearchType] = useState("PRODUCT");

  const handleSearchTypeChange = (event) => {
    const newSearchType = event.target.value;
    setSearchType(newSearchType);
    onOrderShipmentsCustomSearch(orderShipmentsSearchText, newSearchType);
  };

  useEffect(() => {
    localStorage.setItem(SEARCH_TEXT, orderShipmentsSearchText);
  }, [orderShipmentsSearchText]);

  // Function to determine if the search text is a barcode or not
  const determineSearchType = (searchText: string, currentSearchType: string) => {
    const isNumeric = /^[0-9]+$/.test(searchText);
    const isValidLength = searchText.length === 12 || searchText.length === 13;
    const isBarcode = isNumeric && isValidLength;

    if (isBarcode && currentSearchType === "PRODUCT") {
      return "BARCODE";
    } else if (!isBarcode && currentSearchType === "PRODUCT") {
      return "PRODUCT";
    } else {
      return currentSearchType;
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    // Determine the correct search type (either "BARCODE" or "PRODUCT")
    const newSearchType = determineSearchType(orderShipmentsSearchText, searchType);

    // Call the search function with the correct search type
    onOrderShipmentsCustomSearch(orderShipmentsSearchText, newSearchType);
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <AntTabs
        value={shipmentTabValue}
        onChange={handleShipmentsTabChange}
        aria-label="Shipment tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <AntTab label={SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.label} value={SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.value} />
        <AntTab label={SHIPMENTS_TABLE_TABS.CLICK_AND_COLLECT.label} value={SHIPMENTS_TABLE_TABS.CLICK_AND_COLLECT.value} />
        <AntTab label={SHIPMENTS_TABLE_TABS.ERROR.label} value={SHIPMENTS_TABLE_TABS.ERROR.value} />
        <AntTab label={SHIPMENTS_TABLE_TABS.PICK_AND_PACK.label} value={SHIPMENTS_TABLE_TABS.PICK_AND_PACK.value} />
        <AntTab label={SHIPMENTS_TABLE_TABS.SHIPPED.label} value={SHIPMENTS_TABLE_TABS.SHIPPED.value} />
        <AntTab label={SHIPMENTS_TABLE_TABS.ALL_ORDERS.label} value={SHIPMENTS_TABLE_TABS.ALL_ORDERS.value} />
        <AntTab label={SHIPMENTS_TABLE_TABS.PRINT_HUB.label} value={SHIPMENTS_TABLE_TABS.PRINT_HUB.value} />
      </AntTabs>

      <Stack
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        spacing={0.5}
        component="form"
        onSubmit={handleSearch} // Use handleSearch for form submission
        sx={{
          width: 500,
        }}
      >
        <IconButton type="submit" aria-label="search" size={"small"}>
          <SearchIcon sx={{ width: "16px", height: "16px" }} />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1, fontSize: "14px" }}
          placeholder="Search order shipments"
          inputProps={{ "aria-label": "search order shipments" }}
          size="small"
          value={orderShipmentsSearchText}
          onChange={(e) => setOrderShipmentsSearchText(e.target.value)}
          disabled={shipmentTabValue === SHIPMENTS_TABLE_TABS.PRINT_HUB.value}
        />
        <Select
          labelId="sort-by-field-label-id"
          id="sort-by-field-id"
          value={searchType}
          onChange={handleSearchTypeChange}
          disabled={shipmentTabValue === SHIPMENTS_TABLE_TABS.PRINT_HUB.value}
          sx={{
            ".MuiSelect-select": {
              padding: 1,
              width: "100px",
            },
            fontSize: "0.75rem",
          }}
        >
          {Object.entries(ORDER_SHIPMENTS_SEARCH_TYPES).map(([key, value]) => (
            <MenuItem value={key} key={`${key}-menu-item`}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            </MenuItem>
          ))}
        </Select>
        <Divider orientation="vertical" variant="middle" flexItem />
        <Tooltip title={shipmentTabValue === SHIPMENTS_TABLE_TABS.PRINT_HUB.value ? "Not allowed on custom tabs" : ""}>
          {/* span is required here as disabled button do not emit any events and is not allowed by Tooltip */}
          <span>
            <Button
              onClick={onAdvancedSettingsOpen}
              style={{ textTransform: "none" }}
              disabled={shipmentTabValue === SHIPMENTS_TABLE_TABS.PRINT_HUB.value}
            >
              <Stack direction="row" justifyContent="space-around" alignItems="center" spacing={0.5}>
                <AdvancedSearchIcon
                  fill={shipmentTabValue != SHIPMENTS_TABLE_TABS.PRINT_HUB.value ? globalTheme.blue : globalTheme.border}
                  width="16"
                  height="16"
                />
                <Typography
                  variant="caption"
                  color={shipmentTabValue != SHIPMENTS_TABLE_TABS.PRINT_HUB.value ? globalTheme.blue : globalTheme.border}
                >
                  Advanced Settings
                </Typography>
              </Stack>
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
